import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ChevronRight, Check } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";

/**
 * Relationship Intelligence Calibration — the premium, conversational
 * onboarding flow. NOT a personality quiz. Positioned as a calibration of
 * the system's understanding of HER.
 */
export default function Calibration() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [responses, setResponses] = useState({}); // {qid: option_id}
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/calibration/questions")
      .then((r) => setQuestions(r.data?.questions || []))
      .catch(() => setError("Couldn't load the calibration. Please try again."));
  }, []);

  const total = questions.length || 1;
  const q = questions[idx];
  const picked = q ? responses[q.id] : null;
  const progress = Math.round(((idx + (picked ? 1 : 0)) / total) * 100);

  const pick = (oid) => {
    setResponses((s) => ({ ...s, [q.id]: oid }));
    // Auto-advance with a small delay so the selection lands visibly first
    setTimeout(() => {
      if (idx + 1 < total) setIdx(idx + 1);
    }, 280);
  };

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const payload = {
        responses: Object.entries(responses).map(([question_id, option_id]) => ({ question_id, option_id })),
      };
      const r = await api.post("/calibration/submit", payload);
      setReveal(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save. Please try again.");
    } finally { setSubmitting(false); }
  };

  // ── Reveal screen ────────────────────────────────────────────────────
  if (reveal) {
    const p = reveal.primary || {};
    const s = reveal.secondary;
    const conf = Math.round((reveal.ontology_state?.confidence || 0) * 100);
    return (
      <div data-testid="calibration-reveal" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <SeoHead title="Your relationship intelligence read · SugarSense" description="Your initial calibration." />
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Your initial read</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-3 leading-tight">
            {p.name}
          </h1>
          <p className="mt-3 text-cocoa/65 italic">{p.tagline}</p>
        </div>

        <div className="ss-card-premium p-6 sm:p-8 space-y-6">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">You tend to…</p>
            <p className="text-cocoa text-[15px] leading-relaxed">{p.tendency}</p>
          </div>

          {p.security_anchors?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">What helps you feel secure</p>
              <ul className="space-y-1.5">
                {p.security_anchors.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-cocoa/85 text-[14px]">
                    <Check size={12} className="text-sage mt-1 shrink-0" /><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {p.growth_invitation && (
            <div className="border-t border-champagne pt-5">
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">A quiet invitation</p>
              <p className="text-cocoa/75 text-[14px] leading-relaxed italic">{p.growth_invitation}</p>
            </div>
          )}

          {s && (
            <div className="border-t border-champagne pt-5">
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">Secondary influence</p>
              <p className="text-cocoa font-medium">{s.name}</p>
              <p className="text-cocoa/65 text-[13px] mt-1">{s.tagline}</p>
            </div>
          )}

          <div className="border-t border-champagne pt-5 flex items-baseline justify-between">
            <p className="text-[11px] text-cocoa/55">Initial confidence</p>
            <p className="text-cocoa font-medium">{conf}%</p>
          </div>
          <p className="text-[11px] text-cocoa/50 italic leading-relaxed">
            This is a starting point, not a label. Your read evolves as SugarSense observes the patterns of your relationships over time.
          </p>
        </div>

        <div className="mt-6 flex gap-3 flex-col sm:flex-row">
          <button onClick={() => nav("/ask")} data-testid="calibration-reveal-ask" className="ss-pill ss-pill-gold h-12 flex-1 justify-center">
            <Sparkles size={14} /> Begin with my first read
          </button>
          <button onClick={() => nav("/self-insight")} data-testid="calibration-reveal-insight" className="ss-pill h-12 flex-1 justify-center">
            See my full insight
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────
  if (!q) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <p className="text-cocoa/60 text-sm">Preparing your calibration…</p>
        {error && <p className="mt-3 text-mauve text-sm">{error}</p>}
      </div>
    );
  }

  // ── Question screen ──────────────────────────────────────────────────
  const isLast = idx + 1 === total;
  const allAnswered = Object.keys(responses).length >= total;
  return (
    <div data-testid="calibration-page" className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <SeoHead title="Relationship Intelligence Calibration · SugarSense" description="A reflective conversation about how you tend to operate in relationships." />

      <div className="text-center mb-8">
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Relationship Intelligence Calibration</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-cocoa mt-2 leading-tight">
          A quiet conversation, before we begin.
        </h1>
        <p className="mt-2 text-cocoa/65 text-[13px] max-w-md mx-auto italic">
          Not a test. Reflections that help SugarSense read your patterns more accurately.
        </p>
      </div>

      {/* Progress */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[11px] tracking-[0.3em] uppercase text-cocoa/55">Question {idx + 1} of {total}</span>
          <span data-testid="calibration-progress" className="text-[11px] text-cocoa/55 tabular-nums">{progress}%</span>
        </div>
        <div className="h-1 rounded-full bg-champagne overflow-hidden">
          <div className="h-full bg-mauve transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Question */}
      <div data-testid={`calibration-q-${idx}`} className="ss-card-premium p-6 sm:p-8 transition-opacity">
        <p className="font-heading text-xl sm:text-2xl text-cocoa leading-snug">{q.prompt}</p>
        {q.subcopy && <p className="text-[13px] text-cocoa/60 italic mt-2 leading-relaxed">{q.subcopy}</p>}
        <div className="mt-5 space-y-2">
          {q.options.map((o, i) => (
            <button key={o.id} onClick={() => pick(o.id)}
              data-testid={`calibration-option-${idx}-${o.id}`}
              className={`w-full text-left p-4 rounded-md border transition-all duration-200 ${picked === o.id ? "bg-mauve/10 border-mauve text-cocoa" : "bg-ivory border-champagne hover:border-mauve/60 text-cocoa/85"}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[14px] leading-relaxed">{o.label}</span>
                {picked === o.id && <Check size={14} className="text-mauve shrink-0" />}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => idx > 0 && setIdx(idx - 1)} disabled={idx === 0}
          data-testid="calibration-back"
          className="text-[13px] text-cocoa/60 hover:text-cocoa disabled:opacity-30 inline-flex items-center gap-1">
          ← Previous
        </button>
        {isLast && allAnswered ? (
          <button onClick={submit} disabled={submitting}
            data-testid="calibration-submit"
            className="ss-pill ss-pill-gold h-11 disabled:opacity-50">
            {submitting ? "Reading your patterns…" : <>Reveal my read <ChevronRight size={14} /></>}
          </button>
        ) : !isLast ? (
          <button onClick={() => picked && setIdx(idx + 1)} disabled={!picked}
            data-testid="calibration-next"
            className="text-[13px] text-cocoa/60 hover:text-cocoa disabled:opacity-30 inline-flex items-center gap-1">
            Next →
          </button>
        ) : (
          <span className="text-[11px] text-cocoa/45 italic">Answer all questions to continue</span>
        )}
      </div>
      {error && <p data-testid="calibration-error" className="text-mauve text-sm mt-3 text-center">{error}</p>}
    </div>
  );
}
